import { FormFieldLabel } from '@alison/ui';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import SortOutlined from '@mui/icons-material/SortOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import type { CreativeMedia } from '../../lib/workflows.hooks.js';
import { CreativeScoreCard } from './CreativeScoreCard.js';
import { type CreativeRun, runsFor } from './creativeRuns.js';
import { LABEL_SX } from './ScoreVisuals.js';
import { SegmentedControl } from './SegmentedControl.js';
import {
  type CreativeOrder,
  creativesOn,
  type ScoredBatchPlatform,
  sortedCreatives,
} from './scoredBatch.js';

const CARD_MIN = 320;

const ORDERS: Array<{ id: CreativeOrder; label: string }> = [
  { id: 'high', label: 'Score — high to low' },
  { id: 'low', label: 'Score — low to high' },
  { id: 'attention', label: 'Needs attention first' },
  { id: 'name', label: 'Name — A to Z' },
];

const SORT_SX = {
  gap: 1,
  px: 2,
  py: 1,
  borderRadius: 999,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  textTransform: 'none',
  '&:hover': { bgcolor: 'background.paper' },
};

export interface CreativeGridProps {
  result: unknown;
  platforms: ScoredBatchPlatform[];
  media: Record<string, CreativeMedia>;
  runs?: CreativeRun[];
  onOpenCreative?: (creativeId: string, networkId: number) => void;
}

function orderIcon(order: CreativeOrder): ReactNode {
  if (order === 'attention') return <WarningAmberOutlined sx={{ fontSize: 20 }} />;
  const flip = order === 'name' ? 'scaleY(-1)' : undefined;
  return <SortOutlined sx={{ fontSize: 20, transform: flip }} />;
}

export function CreativeGrid({
  result,
  platforms,
  media,
  runs = [],
  onOpenCreative,
}: CreativeGridProps): ReactNode {
  const [openNetworkId, setOpenNetworkId] = useState<number | null>(null);
  const [order, setOrder] = useState<CreativeOrder>('high');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const tab = platforms.find((platform) => platform.networkId === openNetworkId) ?? platforms[0];
  const creatives = sortedCreatives(
    tab === undefined ? [] : creativesOn(result, tab.networkId),
    order,
  );
  const chosen = ORDERS.find((entry) => entry.id === order) ?? ORDERS[0];

  return (
    <Stack sx={{ gap: 3 }}>
      <Stack
        direction="row"
        sx={{ gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <SegmentedControl<number>
          label="Platform"
          role="tab"
          value={tab?.networkId ?? -1}
          onChange={setOpenNetworkId}
          options={platforms.map((platform) => ({ id: platform.networkId, label: platform.label }))}
        />

        <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 13, color: 'text.muted' }}>
            {creatives.length} creative{creatives.length === 1 ? '' : 's'}
          </Typography>
          <Button
            onClick={(event) => setAnchor(event.currentTarget)}
            endIcon={
              <Box component="span" sx={{ fontSize: 11 }}>
                ▾
              </Box>
            }
            sx={SORT_SX}
          >
            <FormFieldLabel sx={{ ...LABEL_SX, color: 'text.muted' }}>SORT BY</FormFieldLabel>
            <Typography sx={{ fontSize: 14 }}>{chosen?.label}</Typography>
          </Button>
          <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
            {ORDERS.map((entry) => (
              <MenuItem
                key={entry.id}
                selected={entry.id === order}
                onClick={() => {
                  setOrder(entry.id);
                  setAnchor(null);
                }}
                sx={{ gap: 1.5, fontSize: 15 }}
              >
                {orderIcon(entry.id)}
                <Box sx={{ flex: 1 }}>{entry.label}</Box>
                {entry.id === order ? (
                  <CheckOutlined sx={{ fontSize: 18, color: 'primary.main' }} />
                ) : null}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN}px, 1fr))`,
        }}
      >
        {creatives.map((creative) => (
          <CreativeScoreCard
            key={`${creative.creativeId}-${creative.networkId}`}
            creative={creative}
            media={media[creative.creativeId] ?? null}
            runs={runsFor(runs, creative.creativeId, creative.networkId).length}
            {...(onOpenCreative && tab !== undefined
              ? { onOpen: () => onOpenCreative(creative.creativeId, tab.networkId) }
              : {})}
          />
        ))}
      </Box>
    </Stack>
  );
}
